import React, { useState } from 'react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart,
} from 'recharts';
import MainLayout from '../../components/MainLayout';
import { useTranslation } from '../../context/LanguageContext';
import StepNavC from './StepNavC';
import ConfirmModal from './ConfirmModal';
import {
  recommendedParams,
  paramStatusMeta,
  hasExceed,
  overallStatus,
  overallStatusMeta,
  changeLog,
  changeLogStatusMeta,
  inSilico,
  defectTrend,
  beforeAfterKpi,
} from '../../mock/modulec';
import '../moduleb/moduleb-common.css';
import './ModuleCSafeguard.css';

function ModuleCSafeguard() {
  const { t } = useTranslation();
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [rolledBack, setRolledBack] = useState(false);

  const status = rolledBack ? 'rollback_wait' : overallStatus;
  const statusMeta = overallStatusMeta[status];

  const handleRollback = () => {
    setRolledBack(true);
    setRollbackOpen(false);
  };

  return (
    <MainLayout>
      <div className="mb-container">
        <StepNavC current={2} />

        <div className="mb-page-header">
          <div>
            <div className="mb-page-title">
              <span className="mb-step-tag mc-tag">STEP 3</span>{t('pages.modulec.nav.steps.safeguard.title')}
            </div>
            <div className="mb-page-subtitle">
              {t('pages.modulec.safeguard.subtitle')}
            </div>
          </div>
          <div className="mb-page-meta">
            <span className="mb-badge mc-overall-badge" style={{ color: statusMeta.color, background: statusMeta.bg }}>
              <span className="material-symbols-outlined">{statusMeta.icon}</span>
              {t(statusMeta.labelKey)}
            </span>
          </div>
        </div>

        <div className="mb-body">
          {/* 3중 세이프가드 카드 */}
          <div className="mb-grid mb-grid-3 mc-guard-grid">
            {/* ① 임계초과 자동차단 */}
            <div className={`mb-card mc-guard-card ${hasExceed ? 'blocked' : 'ok'}`}>
              <div className="mb-card-title">
                <span className="material-symbols-outlined">shield</span>{t('pages.modulec.safeguard.guard1')}
                <span className={`mc-guard-state ${hasExceed ? 'blocked' : 'ok'}`}>
                  {hasExceed ? t('pages.modulec.safeguard.blocked') : t('pages.modulec.safeguard.normal')}
                </span>
              </div>
              <div className="mc-guard-params">
                {recommendedParams.map((p) => {
                  const pos = ((p.recommended - p.min) / (p.max - p.min)) * 100;
                  const meta = paramStatusMeta[p.status];
                  return (
                    <div key={p.key} className="mc-guard-param">
                      <div className="mc-guard-param-head">
                        <span className="mc-guard-param-name">{t(p.labelKey)}</span>
                        <span className="mc-guard-param-val" style={{ color: meta.color }}>
                          {p.recommended}{p.unit}
                        </span>
                      </div>
                      <div className="mc-guard-gauge">
                        <div className="mc-guard-gauge-track">
                          <span
                            className={`mc-guard-gauge-marker ${p.status}`}
                            style={{ left: `${Math.max(0, Math.min(100, pos))}%` }}
                          />
                        </div>
                        <div className="mc-guard-gauge-labels">
                          <span>{p.min}</span>
                          <span>{p.max}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={`mc-guard-note ${hasExceed ? 'blocked' : 'ok'}`}>
                <span className="material-symbols-outlined">{hasExceed ? 'block' : 'check_circle'}</span>
                {hasExceed
                  ? t('pages.modulec.safeguard.guard1NoteBlocked')
                  : t('pages.modulec.safeguard.guard1NoteOk')}
              </div>
            </div>

            {/* ② 롤백·변경이력 로그 */}
            <div className="mb-card mc-guard-card">
              <div className="mb-card-title">
                <span className="material-symbols-outlined">history</span>{t('pages.modulec.safeguard.guard2')}
                <button className="mc-rollback-btn" onClick={() => setRollbackOpen(true)} disabled={rolledBack}>
                  <span className="material-symbols-outlined">undo</span>
                  {rolledBack ? t('pages.modulec.safeguard.rollbackRequested') : t('pages.modulec.safeguard.rollbackBtn')}
                </button>
              </div>
              <div className="mb-table-wrap mc-log-wrap">
                <table className="mb-table mc-log-table">
                  <thead>
                    <tr>
                      <th>{t('pages.modulec.safeguard.cols.time')}</th>
                      <th>{t('pages.modulec.safeguard.cols.param')}</th>
                      <th>{t('pages.modulec.safeguard.cols.change')}</th>
                      <th>{t('pages.modulec.safeguard.cols.actor')}</th>
                      <th style={{ textAlign: 'center' }}>{t('pages.modulec.safeguard.cols.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changeLog.map((r, i) => {
                      const meta = changeLogStatusMeta[r.status];
                      return (
                        <tr key={i}>
                          <td className="mb-mono">{r.ts}</td>
                          <td>{t(r.paramKey)}</td>
                          <td className="mb-mono mc-log-change">
                            <span className="mc-log-from">{r.from}</span>
                            <span className="material-symbols-outlined">east</span>
                            <span className="mc-log-to">{r.to}</span>
                          </td>
                          <td>{t(r.actorKey)}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span className="mb-badge" style={{ color: meta.color, background: meta.bg }}>{t(meta.labelKey)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ③ in-silico 사전검증 */}
            <div className="mb-card mc-guard-card">
              <div className="mb-card-title">
                <span className="material-symbols-outlined">biotech</span>{t('pages.modulec.safeguard.guard3')}
                <span className={`mc-guard-state ${inSilico.passed ? 'ok' : 'blocked'}`}>
                  {inSilico.passed ? t('pages.modulec.safeguard.passed') : t('pages.modulec.safeguard.failed')}
                </span>
              </div>
              <div className="mc-silico-compare">
                <div className="mc-silico-item">
                  <span className="mc-silico-label">{t('pages.modulec.safeguard.beforeExpected')}</span>
                  <span className="mc-silico-value before">{inSilico.baselineDefectRate}<small>%</small></span>
                </div>
                <span className="material-symbols-outlined mc-silico-arrow">trending_down</span>
                <div className="mc-silico-item">
                  <span className="mc-silico-label">{t('pages.modulec.safeguard.afterExpected')}</span>
                  <span className="mc-silico-value after">{inSilico.predictedDefectRate}<small>%</small></span>
                </div>
              </div>
              <div className="mc-silico-twin">
                <span className="material-symbols-outlined">deployed_code</span>
                {t('pages.modulec.safeguard.twinVerified')}
              </div>
              <div className="mc-guard-note info">
                <span className="material-symbols-outlined">info</span>
                {t(inSilico.noteKey)}
              </div>
            </div>
          </div>

          {/* 적용 전/후 검증 패널 */}
          <div className="mb-card mb-mt">
            <div className="mb-card-title">
              <span className="material-symbols-outlined">monitoring</span>{t('pages.modulec.safeguard.verifyTitle')}
            </div>
            <div className="mc-verify">
              <div className="mc-verify-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={defectTrend} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="mcRate" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2f38" />
                    <XAxis dataKey="label" stroke="#6b7280" tick={{ fontSize: 10 }} interval={2} />
                    <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} domain={[0, 6]} unit="%" />
                    <Tooltip
                      contentStyle={{ background: '#1a1f28', border: '1px solid #3a3f47', borderRadius: 6, fontSize: 12 }}
                      labelStyle={{ color: '#9CA3AF' }}
                      formatter={(v) => [`${v}%`, t('pages.modulec.safeguard.defectRate')]}
                    />
                    <ReferenceLine x={t('pages.modulec.safeguard.hourLabel', { h: defectTrend[beforeAfterKpi.appliedAtIdx].hour })} stroke="#3B82F6" strokeDasharray="4 3"
                      label={{ value: t('pages.modulec.safeguard.applied'), fill: '#3B82F6', fontSize: 11, position: 'top' }} />
                    <Area type="monotone" dataKey="rate" stroke="#22c55e" strokeWidth={2} fill="url(#mcRate)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="mc-verify-kpis">
                <div className="mc-verify-reduction">
                  <span className="mc-verify-reduction-label">{t('pages.modulec.safeguard.reduction')}</span>
                  <span className="mc-verify-reduction-value">{beforeAfterKpi.reduction}%</span>
                  <span className="mc-verify-reduction-sub">
                    {beforeAfterKpi.beforeRate}% → {beforeAfterKpi.afterRate}%
                  </span>
                </div>
                <div className="mc-verify-stat">
                  <span className="mc-verify-stat-label">{t('pages.modulec.safeguard.significance')}</span>
                  <span className="mb-badge mc-pvalue">
                    <span className="material-symbols-outlined">verified</span>
                    t-test p = {beforeAfterKpi.pValue} &lt; 0.05
                  </span>
                </div>
                <div className="mc-verify-stat">
                  <span className="mc-verify-stat-label">{t('pages.modulec.safeguard.cumulativeShots')}</span>
                  <span className="mc-verify-stat-value">{beforeAfterKpi.cumulativeShots.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 롤백 확인 모달 */}
      <ConfirmModal
        isOpen={rollbackOpen}
        type="danger"
        title={t('pages.modulec.safeguard.rollbackTitle')}
        message={t('pages.modulec.safeguard.rollbackMessage')}
        changes={recommendedParams.map((p) => ({ labelKey: p.labelKey, from: p.recommended, to: p.current, unit: p.unit }))}
        warning={t('pages.modulec.safeguard.rollbackWarning')}
        confirmText={t('pages.modulec.safeguard.rollbackConfirm')}
        cancelText={t('pages.modulec.modal.cancel')}
        onCancel={() => setRollbackOpen(false)}
        onConfirm={handleRollback}
      />
    </MainLayout>
  );
}

export default ModuleCSafeguard;
